import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {OverlayDirectionProvider} from '@github-ui/copilot-chat/contexts/OverlayDirectionContext'
import {useChatStateValues} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {
  copilotDockedPanelClosedEvent,
  copilotDockedPanelID,
  copilotDockedPanelReadyEvent,
} from '@github-ui/copilot-chat/utils/constants'
import {
  AddCopilotChatReferenceEvent,
  OpenCopilotChatEvent,
  SearchCopilotEvent,
  subscribeAddCopilotChatReference,
  subscribeSearchCopilot,
  subscribeSymbolChanged,
  SymbolChangedEvent,
} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import type {ResizePositionHandlers} from '@github-ui/copilot-chat/utils/copilot-chat-hooks'
import {useDraggablePanel, useResizablePanel} from '@github-ui/copilot-chat/utils/copilot-chat-hooks'
import type {CopilotDeferredPayload} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {CopilotChatIntents, DialogType, ModeSupportName} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {useDefaultModel} from '@github-ui/copilot-chat/utils/models'
import {
  useContentPreview,
  useContentPreviewOpen,
} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import {CopilotImmersiveProviders} from '@github-ui/copilot-immersive-v1/components/CopilotImmersiveProviders'
import type {CopilotImmersivePayload} from '@github-ui/copilot-immersive-v1/routes/payloads'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {useQuery} from '@github-ui/react-query'
import {RelayEnvironmentProvider, relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {Dialog} from '@primer/react'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {ChatPanel} from './Components/ChatPanel'
import {CopilotChatPanelContent} from './Components/CopilotChatPanelContent'
import {DockedCopilotChatPortal} from './Components/DockedCopilotChatPortal'
import {EmbeddedContentPreview} from './Components/EmbeddedContentPreview'
import {Header} from './Components/Header'
import styles from './CopilotImmersiveEmbedded.module.css'

const MAX_PANEL_WIDTH = 767
const environment = relayEnvironmentWithMissingFieldHandlerForNode()

export function useDeferredPayload(deferredPayloadUrl: string | undefined) {
  const {chatIsOpen} = useChatStateValues('chatIsOpen')

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister
  const {data} = useQuery({
    queryKey: ['copilot-deferred-payload', deferredPayloadUrl],
    queryFn: async () => {
      const response = await verifiedFetchJSON(deferredPayloadUrl as string)
      if (!response.ok) {
        throw new Error(`Deferred payload fetch failed (${response.status})`)
      }
      return (await response.json()) as CopilotDeferredPayload
    },
    enabled: chatIsOpen && !!deferredPayloadUrl,
    staleTime: Infinity,
    retry: 2,
  })

  return data ?? null
}

export interface CopilotImmersiveEmbeddedProps {
  payload: CopilotImmersivePayload
  pendingOpenEvent?: OpenCopilotChatEvent | null
  onPendingEventHandled?: () => void
}

export function CopilotImmersiveEmbedded({
  payload,
  pendingOpenEvent,
  onPendingEventHandled,
}: CopilotImmersiveEmbeddedProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [deferredPayload, setDeferredPayload] = useState<CopilotDeferredPayload | null>(null)

  // Merge deferred payload into the lite payload when it arrives.
  // Before deferred data loads, the component uses default values provided by CopilotChatLoader.
  const mergedPayload = useMemo(() => {
    if (!deferredPayload) return payload
    return {
      ...payload,
      ...deferredPayload,
    }
  }, [payload, deferredPayload])

  return (
    <RelayEnvironmentProvider environment={environment}>
      <AppPayloadContext value={mergedPayload}>
        <CopilotImmersiveProviders payload={mergedPayload} mode="assistive">
          <CopilotHeaderButton
            inputRef={inputRef}
            pendingOpenEvent={pendingOpenEvent}
            onPendingEventHandled={onPendingEventHandled}
            deferredPayloadUrl={payload.deferredPayloadUrl}
            onDeferredPayloadLoaded={setDeferredPayload}
          />
        </CopilotImmersiveProviders>
      </AppPayloadContext>
    </RelayEnvironmentProvider>
  )
}

type CopilotHeaderButtonProps = {
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  pendingOpenEvent?: OpenCopilotChatEvent | null
  onPendingEventHandled?: () => void
  deferredPayloadUrl?: string
  onDeferredPayloadLoaded?: (payload: CopilotDeferredPayload) => void
}

function shouldShowPendingMessageLoading(event: OpenCopilotChatEvent): boolean {
  switch (event.payload.intent) {
    case CopilotChatIntents.explain:
    case CopilotChatIntents.suggest:
    case CopilotChatIntents.explainFileDiff:
    case CopilotChatIntents.actionsAgent:
      return true
    default:
      return false
  }
}

const CopilotHeaderButton = ({
  inputRef,
  pendingOpenEvent,
  onPendingEventHandled,
  deferredPayloadUrl,
  onDeferredPayloadLoaded,
}: CopilotHeaderButtonProps) => {
  const manager = useChatManager()
  const state = useChatStateValues(
    'selectedThreadID',
    'threads',
    'currentTopic',
    'currentView',
    'availableModels',
    'chatVisibleSettingPath',
    'chatIsOpen',
    'threadsLoading',
  )
  const selectedThread = getSelectedThread(state)
  const {currentTopic} = state
  const {screenSize} = useScreenSize()
  const {plan, premiumInteractionsQuotaExceeded, overagesEnabled} = useEntitlement()
  const defaultModel = useDefaultModel(state.availableModels)
  const initialRenderRef = useRef(true)
  const hasPersistedRef = useRef(false)
  const pendingMessageLoadingTimeoutRef = useRef<number | null>(null)
  const [isPendingMessageLoading, setIsPendingMessageLoading] = useState(false)
  // Track whether we're in the initial loading state (reopening chat from localStorage)
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    // Only show loading if we're going to reopen chat from localStorage
    return !copilotLocalStorage.getCollapsedState() && screenSize > ScreenSize.large
  })

  const clearPendingMessageLoadingTimeout = useCallback(() => {
    if (pendingMessageLoadingTimeoutRef.current !== null) {
      window.clearTimeout(pendingMessageLoadingTimeoutRef.current)
      pendingMessageLoadingTimeoutRef.current = null
    }
  }, [])

  const clearPendingMessageLoading = useCallback(() => {
    clearPendingMessageLoadingTimeout()
    setIsPendingMessageLoading(false)
  }, [clearPendingMessageLoadingTimeout])

  const schedulePendingMessageLoadingTimeout = useCallback(() => {
    clearPendingMessageLoadingTimeout()
    pendingMessageLoadingTimeoutRef.current = window.setTimeout(() => {
      pendingMessageLoadingTimeoutRef.current = null
      setIsPendingMessageLoading(false)
    }, 10_000)
  }, [clearPendingMessageLoadingTimeout])

  useEffect(() => {
    return () => {
      clearPendingMessageLoadingTimeout()
    }
  }, [clearPendingMessageLoadingTimeout])

  useEffect(() => {
    if (!pendingOpenEvent) return

    const eventStartsWithMessage = shouldShowPendingMessageLoading(pendingOpenEvent)

    const handlePendingOpenEvent = async () => {
      if (eventStartsWithMessage) {
        setIsPendingMessageLoading(true)
        schedulePendingMessageLoadingTimeout()
      }

      // Clear the pending event in the parent immediately so that state changes
      // triggered by handleOpenPanelEvent (chatIsOpen, selectedThread, etc.) do
      // not cause this effect to re-run and fire a second concurrent open call.
      onPendingEventHandled?.()

      // Send analytics for header button clicks
      if (pendingOpenEvent.payload.source === 'header') {
        sendEvent('dotcom_chat.activate', {
          target: 'GLOBAL_COPILOT_MENU_HEADER_TO_IMMERSIVE',
          mode: 'global_nav',
        })
      }
      // When opened from anywhere except the global nav header button,
      // switch to ask mode if currently in agent mode
      if (
        pendingOpenEvent.payload.source !== 'header' &&
        copilotLocalStorage.getActiveMode() === ModeSupportName.Agent
      ) {
        copilotLocalStorage.setActiveMode(ModeSupportName.Ask)
      }
      const entitlement = {plan, premiumInteractionsQuotaExceeded, overagesEnabled}
      try {
        await manager.handleOpenPanelEvent(
          selectedThread,
          pendingOpenEvent,
          state.chatIsOpen,
          currentTopic,
          defaultModel,
          entitlement,
        )
      } catch {
        sendEvent('dotcom_chat.activate.error', {
          target: 'ERROR_PENDING_OPEN_EVENT',
          mode: 'assistive',
          source: pendingOpenEvent.payload.source,
          intent: pendingOpenEvent.payload.intent,
          message: 'Failed to handle pending open event.',
        })
        clearPendingMessageLoading()
      }
      // Loading state is cleared by EmbeddedImmersive once the message enters
      // the send pipeline (pendingFirstMessage or isWaitingOnCopilot is set),
      // or by the fallback timeout/rejection handler if that never happens.
    }

    void handlePendingOpenEvent()
  }, [
    pendingOpenEvent,
    onPendingEventHandled,
    manager,
    selectedThread,
    state.chatIsOpen,
    currentTopic,
    defaultModel,
    plan,
    premiumInteractionsQuotaExceeded,
    overagesEnabled,
    schedulePendingMessageLoadingTimeout,
    clearPendingMessageLoading,
  ])

  useEffect(() => {
    return subscribeAddCopilotChatReference((e: AddCopilotChatReferenceEvent) => {
      void manager.handleAddReferenceEvent(e)
    })
  }, [manager])

  useEffect(() => {
    return subscribeSymbolChanged((e: SymbolChangedEvent) => {
      void manager.handleSymbolChangedEvent(e)
    })
  }, [manager])

  useEffect(() => {
    return subscribeSearchCopilot((e: SearchCopilotEvent) => {
      manager.handleSearchCopilotEvent(e)
    })
  }, [manager])

  // Run once on initial render to check if we should open chat on page load
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false

      // When the docked panel feature is enabled and we're on a PR page that supports
      // the side panel, skip auto-reopening the embedded panel — chat opens in the side panel instead.
      // Match /:owner/:repo/pull/:number to avoid false positives (e.g. a user named "pull").
      // Similarly, blob/tree views use the copilot side panel, so skip auto-reopening there too.
      const pathname = window.location.pathname
      const isPullRequestPage = /\/[^/]+\/[^/]+\/pull\/\d+/.test(pathname)
      const isFilesChangedView = isPullRequestPage && /\/changes\b/.test(pathname)
      const isBlobOrTreeView =
        isFeatureEnabled('copilot_code_view_side_panel') && /\/[^/]+\/[^/]+\/(blob|tree)\//.test(pathname)
      const isDockedPanelPage =
        isBlobOrTreeView ||
        (isFeatureEnabled('copilot_chat_docked_panel') &&
          (isFilesChangedView || (isPullRequestPage && isFeatureEnabled('updated_pull_request_header'))))

      if (!copilotLocalStorage.getCollapsedState() && screenSize > ScreenSize.large && !isDockedPanelPage) {
        // Create a synthetic event to trigger proper thread loading
        // Use source: 'header' to preserve the current mode (don't switch from Task to Ask)
        // Use attachThread: true so that handleOpenPanelEvent restores the previously
        // selected thread from localStorage instead of creating a new one.
        const syntheticEvent = new OpenCopilotChatEvent({
          intent: CopilotChatIntents.conversation,
          source: 'header',
          attachThread: true,
          references: [],
        })
        const entitlement = {plan, premiumInteractionsQuotaExceeded, overagesEnabled}
        void manager.handleOpenPanelEvent(
          selectedThread,
          syntheticEvent,
          state.chatIsOpen,
          currentTopic,
          defaultModel,
          entitlement,
        )
      } else {
        // Not reopening, so clear the loading state immediately
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsInitialLoading(false)

        // When skipping because the docked PR panel will handle chat, clear the
        // stored open state so the embedded panel doesn't reopen when the user
        // navigates away from the PR page.
        if (isDockedPanelPage) {
          copilotLocalStorage.setCollapsedState(true)
        }
      }
    }
  }, [
    manager,
    screenSize,
    selectedThread,
    state.chatVisibleSettingPath,
    state.currentView,
    state.chatIsOpen,
    currentTopic,
    defaultModel,
    plan,
    premiumInteractionsQuotaExceeded,
    overagesEnabled,
  ])

  // Clear initial loading state once we have a selected thread or confirmed there's no thread to restore
  useEffect(() => {
    if (isInitialLoading && (selectedThread || !state.selectedThreadID)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsInitialLoading(false)
    }
  }, [isInitialLoading, selectedThread, state.chatIsOpen, state.selectedThreadID, state.threadsLoading])

  // Persist chat open state to localStorage when it changes
  // This allows the chat to reopen after hard navigations
  useEffect(() => {
    // Skip the first run to avoid overwriting saved state before we read it in the initial render effect
    if (!hasPersistedRef.current) {
      hasPersistedRef.current = true
      return
    }
    // When the docked PR side panel is handling chat, force collapsed so the
    // embedded panel doesn't reopen when the user navigates away from the PR page.
    if (document.getElementById(copilotDockedPanelID)) {
      copilotLocalStorage.setCollapsedState(true)
      return
    }
    copilotLocalStorage.setCollapsedState(!state.chatIsOpen)
  }, [state.chatIsOpen])

  // Fetch cache-dependent payload fields when the panel opens.
  // This avoids hitting the Copilot user cache on every page load.
  const deferredData = useDeferredPayload(deferredPayloadUrl)

  useEffect(() => {
    if (deferredData) {
      onDeferredPayloadLoaded?.(deferredData)
    }
  }, [deferredData, onDeferredPayloadLoaded])

  return (
    <div className={styles.CopilotChatContainer} data-testid="copilot-immersive-embedded-header-button">
      <EmbeddedImmersive
        inputRef={inputRef}
        isInitialLoading={isInitialLoading}
        isPendingMessageLoading={isPendingMessageLoading}
        onClearPendingMessageLoading={clearPendingMessageLoading}
      />
      <DockedCopilotChatPortal />
    </div>
  )
}

function EmbeddedImmersive({
  inputRef,
  isInitialLoading,
  isPendingMessageLoading,
  onClearPendingMessageLoading,
}: {
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  isInitialLoading: boolean
  isPendingMessageLoading: boolean
  onClearPendingMessageLoading: () => void
}) {
  const {chatIsOpen, selectedThreadID, pendingFirstMessage, isWaitingOnCopilot} = useChatStateValues(
    'chatIsOpen',
    'selectedThreadID',
    'pendingFirstMessage',
    'isWaitingOnCopilot',
  )

  // Clear the pending-message loading spinner as soon as the message enters
  // the send pipeline so the user can watch the response stream in.
  useEffect(() => {
    if (isPendingMessageLoading && (pendingFirstMessage || isWaitingOnCopilot)) {
      onClearPendingMessageLoading()
    }
  }, [isPendingMessageLoading, pendingFirstMessage, isWaitingOnCopilot, onClearPendingMessageLoading])

  const isContentPreviewOpen = useContentPreviewOpen()
  const {closePreviewPane} = useContentPreview()
  const manager = useChatManager()
  const isDraggable = copilotFeatureFlags.immersiveEmbeddedDraggable

  // Track whether the docked side panel is active so we suppress the floating ChatPanel
  const [isDockedPanelActive, setIsDockedPanelActive] = useState(false)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsDockedPanelActive(!!document.getElementById(copilotDockedPanelID))
    const onReady = () => setIsDockedPanelActive(true)
    const onClose = () => setIsDockedPanelActive(false)
    document.addEventListener(copilotDockedPanelReadyEvent, onReady)
    document.addEventListener(copilotDockedPanelClosedEvent, onClose)
    return () => {
      document.removeEventListener(copilotDockedPanelReadyEvent, onReady)
      document.removeEventListener(copilotDockedPanelClosedEvent, onClose)
    }
  }, [])

  const positionHandlersRef = useRef<ResizePositionHandlers | null>(null)
  // The floating ChatPanel renders only when chat is open AND the docked
  // side panel is not active (see the JSX below). Mirror that here so the
  // resize hook only performs the `.AppHeader` layout measurement and
  // installs viewport listeners while the panel is actually visible.
  const isPanelVisible = chatIsOpen && !isDockedPanelActive
  const {panelWidth, panelHeight, startResize, onResizerKeyDown} = useResizablePanel({
    maxPanelWidth: MAX_PANEL_WIDTH,
    positionHandlersRef: isDraggable ? positionHandlersRef : undefined,
    isOpen: isPanelVisible,
  })
  const {
    positionRight,
    positionBottom,
    isDragging,
    startDrag,
    onDragHandleKeyDown,
    setPositionRight,
    setPositionBottom,
  } = useDraggablePanel(panelWidth, panelHeight)

  // Bridge position state so the resize hook can adjust position for right/bottom edge resizes.
  // The ref is read only during mouse event handlers, never during render.
  useEffect(() => {
    positionHandlersRef.current = {
      getRight: () => positionRight,
      getBottom: () => positionBottom,
      setRight: setPositionRight,
      setBottom: setPositionBottom,
    }
  }, [positionRight, positionBottom, setPositionRight, setPositionBottom])

  const staffDialogRef = useRef<HTMLDivElement>(null)
  const [showStaffDialog, setShowStaffDialog] = useState<DialogType>(DialogType.None)

  // Force icon-only mode in the toolbar when panel is narrow (below 544px which is the narrow breakpoint)
  const forceIconOnly = panelWidth < 544

  const handleCloseChat = useCallback(
    (usedEscapeKey?: boolean) => {
      manager.closeChat(usedEscapeKey ? 'escape-key' : 'close-button')
    },
    [manager],
  )

  const chatWasEverOpenedRef = useRef(chatIsOpen)
  useEffect(() => {
    if (chatIsOpen) {
      chatWasEverOpenedRef.current = true
    }
  }, [chatIsOpen])

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only report abandonment when the chat was actually opened during this session,
      // otherwise the metric is skewed by page loads where the user never engaged with chat.
      if (!chatWasEverOpenedRef.current) return
      sendEvent('dotcom_chat.chat_abandoned', {
        trigger: 'beforeunload',
        chatIsOpen,
        selectedThreadID,
        userHasInteracted: manager.userHasInteracted,
      })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [chatIsOpen, selectedThreadID, manager])

  const handleCloseContentPreview = useCallback(() => {
    closePreviewPane()
  }, [closePreviewPane])
  return (
    chatIsOpen &&
    !isDockedPanelActive && (
      <OverlayDirectionProvider preferredOverlaySide="outside-top">
        <ChatPanel
          handleClose={handleCloseChat}
          panelWidth={panelWidth}
          panelHeight={panelHeight}
          startResize={startResize}
          onResizerKeyDown={onResizerKeyDown}
          initialFocusRef={inputRef}
          staffDialogRef={staffDialogRef}
          positionRight={isDraggable ? positionRight : undefined}
          positionBottom={isDraggable ? positionBottom : undefined}
          isDragging={isDraggable ? isDragging : undefined}
        >
          <Header
            staffDialogRef={staffDialogRef}
            showStaffDialog={showStaffDialog}
            setShowStaffDialog={setShowStaffDialog}
            onDragStart={isDraggable ? startDrag : undefined}
            onDragKeyDown={isDraggable ? onDragHandleKeyDown : undefined}
            isDragging={isDraggable ? isDragging : undefined}
          />
          <CopilotChatPanelContent
            inputRef={inputRef}
            isInitialLoading={isInitialLoading || isPendingMessageLoading}
            forceIconOnly={forceIconOnly}
          />
        </ChatPanel>
        {isContentPreviewOpen && (
          <Dialog
            className={styles.contentPreviewDialog}
            onClose={handleCloseContentPreview}
            position={{narrow: 'fullscreen', regular: 'fullscreen', wide: 'fullscreen'}}
            /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
            title="Content Preview"
            renderHeader={({dialogLabelId}) => (
              /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
              <span id={dialogLabelId} className="sr-only">
                Content Preview
              </span>
            )}
            renderBody={() => <EmbeddedContentPreview inputRef={inputRef} />}
          />
        )}
      </OverlayDirectionProvider>
    )
  )
}
